/** @jsxRuntime automatic */
import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, Sparkles, X, XCircle, type LucideIcon } from "lucide-react";
import { Collapse, type CollapseAppear, type CollapseDefer } from "./collapse";
import { useAnnounceRef } from "./live-region";
import { cn } from "./utils";

export type CalloutTone = "neutral" | "brand" | "info" | "success" | "warning" | "danger";

const TONE: Record<CalloutTone, { box: string; icon: string; Icon: LucideIcon | null }> = {
  neutral: { box: "border-border bg-sunken text-foreground", icon: "text-muted-foreground", Icon: Info },
  brand: {
    box: "border-[color-mix(in_srgb,var(--brand)_25%,transparent)] bg-brand-subtle text-foreground",
    icon: "text-brand",
    Icon: Sparkles,
  },
  info: { box: "border-info-border bg-info-bg text-info-fg", icon: "text-info", Icon: Info },
  success: { box: "border-success-border bg-success-bg text-success-fg", icon: "text-success", Icon: CheckCircle2 },
  warning: { box: "border-warning-border bg-warning-bg text-warning-fg", icon: "text-warning", Icon: AlertTriangle },
  danger: { box: "border-danger-border bg-danger-bg text-danger-fg", icon: "text-danger", Icon: XCircle },
};

export interface CalloutProps {
  tone?: CalloutTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Replaces the tone's default icon. Pass `null` for no icon. */
  icon?: ReactNode | null;
  /** Action(s) under the text (a Button size="sm", a link). */
  action?: ReactNode;
  /** Shows a dismiss button. */
  onDismiss?: () => void;
  /** Translated label of the dismiss button. */
  dismissLabel?: string;
  /**
   * Announce it when it appears or changes: `polite` through the app's live
   * region (`useAnnounceRef`, spec 07 › Anuncios), `assertive` as
   * `role="alert"` (read on insertion) for an error that blocks. Leave unset
   * for callouts that are part of the page from the start.
   */
  live?: "polite" | "assertive";
  /** `banner`: full-bleed strip (no side borders/radius) for page-level notices. */
  layout?: "inline" | "banner";
  className?: string;
  /**
   * Mount it once and drive this instead of mounting it behind a condition:
   * it opens and closes with Collapse (spec 07 › Movimiento), with its last
   * content while it leaves. Unset, it is simply there. A spacing it takes
   * from a `gap` or `space-y` parent stays until it unmounts: give it
   * inside, on a wrapper.
   */
  show?: boolean;
  /** Collapse's: whether it grows when it mounts shown (`live` by default: only inside a live scope). */
  appear?: CollapseAppear;
  /** Collapse's: `offscreen` for one that goes with no input (a snapshot, a timer). */
  defer?: CollapseDefer;
  /** It closed and left the DOM. */
  onExited?: () => void;
}

/**
 * Feedback surface with an icon: info / success / warning / danger, plus
 * neutral and brand for non-semantic tips. Replaces `.ui-alert` and the
 * hand-made `bg-amber-50 …` panels. Good news is never amber or red.
 */
export function Callout({
  tone = "info",
  title,
  children,
  icon,
  action,
  onDismiss,
  dismissLabel,
  live,
  layout = "inline",
  className,
  show,
  appear,
  defer,
  onExited,
}: CalloutProps) {
  const t = TONE[tone];
  const announceRef = useAnnounceRef<HTMLDivElement>("polite", live === "polite");
  const DefaultIcon = t.Icon;
  const iconNode =
    icon === null ? null : icon !== undefined ? icon : DefaultIcon ? <DefaultIcon size={16} strokeWidth={1.75} /> : null;
  const box = (
    <div
      ref={live === "polite" ? announceRef : undefined}
      role={live === "assertive" ? "alert" : undefined}
      data-tone={tone}
      className={cn(
        "flex gap-3 border text-label",
        layout === "inline" ? "rounded-lg px-3.5 py-3" : "border-x-0 px-4 py-2.5",
        t.box,
        className,
      )}
    >
      {iconNode ? (
        <span className={cn("mt-px flex shrink-0 [&_svg]:size-4", t.icon)} aria-hidden="true">
          {iconNode}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={cn(title && "mt-0.5", "opacity-90")}>{children}</div> : null}
        {action ? <div className="mt-2.5 flex flex-wrap items-center gap-2">{action}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="-m-1.5 flex size-11 shrink-0 items-center justify-center self-start rounded-md opacity-70 transition-opacity hover:opacity-100 md:pointer-fine:size-7"
        >
          <X size={16} strokeWidth={1.75} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
  if (show === undefined) return box;
  return (
    <Collapse open={show} appear={appear} defer={defer} onExited={onExited}>
      {box}
    </Collapse>
  );
}

/** Page-level notice: a full-bleed Callout strip (announcement, offline, radar transition). */
export function Banner(props: Omit<CalloutProps, "layout">) {
  return <Callout {...props} layout="banner" />;
}
